/**
 * Helpers for preserving breadcrumb context across navigations.
 * Pass `breadcrumbParents` in location.state for intermediate crumbs (e.g. building → application).
 */

export const BREADCRUMB_PARENTS_STATE_KEY = 'breadcrumbParents';

/**
 * @param {Array<{ name: string, link: string, state?: object }>} parents
 * @param {object} [baseState]
 */
export function withBreadcrumbParents(parents, baseState = {}) {
  return {
    ...baseState,
    [BREADCRUMB_PARENTS_STATE_KEY]: parents,
  };
}

/**
 * Prepends Dashboard as origin when drilling from dashboard metrics.
 * @param {object} [baseState]
 */
export function withDashboardBreadcrumbOrigin(baseState = {}) {
  const existing = baseState[BREADCRUMB_PARENTS_STATE_KEY] || [];
  const hasDashboard = existing.some((p) => p.link === '/dashboard');
  if (hasDashboard) {
    return baseState;
  }
  return withBreadcrumbParents([{ name: 'Dashboard', link: '/dashboard' }, ...existing], baseState);
}

/**
 * @param {import('react-router').Location['state']} state
 * @returns {Array<{ name: string, link: string, state?: object }>}
 */
export function getBreadcrumbParentsFromState(state) {
  const parents = state?.[BREADCRUMB_PARENTS_STATE_KEY];
  return Array.isArray(parents) ? parents : [];
}

/**
 * Building-scoped parent crumb for entity list items opened from a property detail tab.
 */
export function buildBuildingBreadcrumbParent(building, buildingId, tabSlug = null) {
  if (!building?.name || !buildingId) return null;
  const base = `/property/properties/${buildingId}/detail`;
  return {
    name: building.name,
    link: tabSlug ? `${base}/${tabSlug}` : base,
  };
}

/**
 * Operator-scoped parent for flows under /user/operator/:landlordId/detail.
 */
export function buildCompanyBreadcrumbParent(landlord, landlordId) {
  const name = landlord?.name || landlord?.company_name;
  if (!name || !landlordId) return null;
  return {
    name,
    link: `/user/operator/${landlordId}/detail`,
  };
}

/**
 * Building parent crumb scoped to the current detail context (property vs operator vs user role).
 */
export function buildScopedBuildingBreadcrumbParent(building, context = {}) {
  const buildingId = building?.building_id;
  const name = building?.name;
  if (!name || !buildingId) return null;

  if (context.landlordId) {
    return {
      name,
      link: `/user/operator/${context.landlordId}/detail/properties`,
    };
  }
  if (context.userRoleBasePath) {
    return {
      name,
      link: `${context.userRoleBasePath}/detail/buildingDetail/${buildingId}`,
    };
  }
  return buildBuildingBreadcrumbParent(building, buildingId);
}
